import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import fetchWithToken from '../../services';
import LocationPieChart from './LocationPieChart';
import TipHistoryTable from './TipHistoryTable';
import Sidebar from '../Sidebar';

function Analytics() {
  const location = useLocation();
  const navigate = useNavigate();
  const chartTabRef = React.useRef(null);
  const historyTabRef = React.useRef(null);
  const chartPanelRef = React.useRef(null);
  const historyPanelRef = React.useRef(null);

  // Get tab from URL query parameter, fall back to localStorage if no query param exists, then default to 'chart'
  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');

    // If URL has a tab parameter, use it
    if (tab && (tab === 'chart' || tab === 'history')) {
      return tab;
    }

    // Otherwise fall back to localStorage if it exists
    const storedTab = localStorage.getItem('tipl_analyticsActiveTab');
    if (storedTab && (storedTab === 'chart' || storedTab === 'history')) {
      // Update URL to match localStorage (on the next render cycle to avoid issues)
      setTimeout(() => {
        navigate(`/analytics?tab=${storedTab}`, { replace: true });
      }, 0);
      return storedTab;
    }

    // Default to 'chart' if neither URL nor localStorage has a valid tab
    return 'chart';
  };

  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [tipHistory, setTipHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Removed pagination state

  // Add this to ensure the component fully initializes before fetching data
  const [initialised, setInitialised] = useState(false);

  // Update URL when tab changes
  const updateTabInUrl = (tab) => {
    // Update URL
    navigate(`/analytics?tab=${tab}`, { replace: true });

    // Also store in localStorage for backward compatibility
    localStorage.setItem('tipl_analyticsActiveTab', tab);

    setActiveTab(tab);
  };

  useEffect(() => {
    // Only update active tab from URL when it's different from current
    const urlTab = getTabFromUrl();
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]); // Only depend on search portion of URL to avoid loops

  useEffect(() => {
    // Set initialized to true after component mounts
    setInitialised(true);
  }, []);

  useEffect(() => {
    console.log('useEffect triggered');
    if (initialised) {
      // Fetch analytics data when component mounts
      fetchAnalyticsData();
      fetchTipHistory();
    }
  }, [initialised]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await fetchWithToken('/get-tip-analytics', {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Analytics data fetched successfully:', data);
      // Store the complete analytics data
      setAnalyticsData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTipHistory = async () => {
    try {
      setLoading(true);
      const url = '/get-tip-history?all=true'; // Request all tips at once
      
      const response = await fetchWithToken(url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('All tip history fetched successfully:', data);
      setTipHistory(data.tips || []); // Store just the tips array
      setError(null);
    } catch (err) {
      console.error('Error fetching tip history:', err);
      setError('Failed to load tip history. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handling removed as we're showing all tips at once

  return (
    <div className=" flex md:min-h-[calc(100vh-3rem)] items-center">
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Main content */}
      {/* <div className="page-background flex-1 md:ml-24 p-4 relative"> */}
      <div className="h-full w-full max-w-4xl mx-auto p-4 relative min-[768px]:ml-48 min-[1250px]:ml-auto md:flex md:items-center">
        <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-4xl mx-auto mt-12 mb-12 min-h-[calc(100vh-10rem)] md:min-h-[500px]">
          {/* <h2 className="text-2xl font-semibold text-gray-800 mb-6">Tip Analytics</h2> */}

          {/* Tab Navigation */}
          <div role="tablist" aria-label="Analytics Tabs" className="flex border-b mb-6 justify-center md:justify-start">
            <button
              id="chart-tab"
              ref={chartTabRef}
              role="tab"
              aria-selected={activeTab === 'chart'}
              aria-controls="chart-panel"
              onClick={() => updateTabInUrl('chart')}
              className={`py-2 px-4 font-medium ${activeTab === 'chart'
                ? 'text-teal-500 border-b-2 border-teal-500'
                : 'text-gray-500 hover:text-teal-500'}`}
            >
              Analysis
            </button>
            <button
              id="history-tab"
              ref={historyTabRef}
              role="tab"
              aria-selected={activeTab === 'history'}
              aria-controls="history-panel"
              onClick={() => updateTabInUrl('history')}
              className={`py-2 px-4 font-medium ${activeTab === 'history'
                ? 'text-teal-500 border-b-2 border-teal-500'
                : 'text-gray-500 hover:text-teal-500'}`}
            >
              Tip history
            </button>
          </div>

          {/* Loading and Error States */}
          {loading && (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Tab Content */}
          {!loading && !error && (
            <>
              {activeTab === 'chart' && (
                <div
                  id="chart-panel"
                  ref={chartPanelRef}
                  role="tabpanel"
                  aria-labelledby="chart-tab"
                  tabIndex={-1}
                  className="mb-6"
                >
                  {/* Check if data is available in the new format or the legacy format */}
                  {analyticsData && ((Array.isArray(analyticsData) && analyticsData.length > 0) || 
                     (!Array.isArray(analyticsData) && analyticsData.byUrl && analyticsData.byUrl.length > 0)) ? (
                    <LocationPieChart data={analyticsData} />
                  ) : (
                    <div className="text-center py-10 text-gray-500">
                      <p>No tip data available yet. Once you start receiving tips, you'll see analytics here.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div
                  id="history-panel"
                  ref={historyPanelRef}
                  role="tabpanel"
                  aria-labelledby="history-tab"
                  tabIndex={-1}
                >
                  {tipHistory.length > 0 ? (
                    <TipHistoryTable
                      tips={tipHistory}
                    />
                  ) : (
                    <div className="text-center py-10 text-gray-500">
                      <p>No tip history available yet. Once you start receiving tips, they'll appear here.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Analytics;
